import { Button } from 'components';
import CustomInput from 'components/CustomInput';
import { useContext, useMemo, useState } from 'react';
import { DisplayFieldType } from 'components/DisplayForms';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { DisplayTextKeys, OffersType, QUERY_KEYS, SetSlideoutConfigType, SlideoutConfigType } from 'views/Trade/types';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { ViewStateType } from 'components/ProductTemplates/types';
import useMutate from 'views/Trade/hooks/useMutate';
import { IEditOffersParams, editOffer } from 'views/Trade/services/editOffer';
import { QUERY_REFECH_TIMEOUT } from 'views/Trade/constants';
import { AppContext } from 'context/ContextProvider';
import { CurrencyFormater } from 'types/commonTypes';
import { buildDisplayText } from 'utils';
import { IDeleteOffersParams, deleteOffer } from 'views/Trade/services/deleteOffer';
import WineInfoTemplate from './WineInfoTemplate';

interface IEditOfferViewProps {
  slideOutConfig: SlideoutConfigType;
  setSlideOutConfig: SetSlideoutConfigType;
}

const EditOfferView = ({ setSlideOutConfig, slideOutConfig }: IEditOfferViewProps) => {
  const {
    state: {
      settings: { accountInfo },
    },
    formatter,
    gbpToTargetCurrency,
  } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);

  const [stopLimitText, setStopLimitText] = useState<number>(
    slideOutConfig?.offer?.stopLimitPriceInTargetCurrency || 0,
  );

  const { isLoading: editOfferIsLoading, mutateAsync: mutateOffer } = useMutate({
    mutationFn: editOffer,
  });

  const { mutateAsync: mutateDeleteOffer } = useMutate({ mutationFn: deleteOffer });

  const updateOffer = async (offer: OffersType, fxRate: number) => {
    const params: IEditOffersParams = {
      body: {
        stopLimitPerUnit: Number((stopLimitText / (fxRate || 1)).toFixed(2)),
        quantity: offer.quantityOutstanding,
      },
      operationType: 'UpdateSellOrder',
      orderId: offer.id,
    };

    try {
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.LOADING,
      }));
      await mutateOffer(params);
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.THANK_YOU,
        templateConfig: { translationKey: 'common:generalSuccess' },
        isOpen: true,
      }));
    } catch (error) {
      const response = error as unknown as { response: { data: { message: string } } };
      const errorMessage = response?.response?.data?.message || '';
      if (
        errorMessage.includes('StopLimitPerUnit') &&
        errorMessage.includes('less') &&
        errorMessage.includes('marketValue')
      ) {
        const messageArray = errorMessage.split(' ');

        const price = messageArray[messageArray.length - 1];
        const formatedPrice = gbpToTargetCurrency.convertAndFormat(Number(price ?? 0));
        // console.log('Price ', price);
        // console.log('formatedPrice ', formatedPrice);

        let displayMessage = `${[...messageArray].slice(0, messageArray.length - 1).join(' ')} ${formatedPrice}`;
        displayMessage = displayMessage.replace('StopLimitPerUnit', 'Stop limit');
        displayMessage = displayMessage.replace('marketValue', 'market value');
        setSlideOutConfig((prev) => ({
          ...prev,
          view: ViewStateType.ERROR_RESULT,
          templateConfig: { subTitle: displayMessage },
          onCTA: () =>
            setSlideOutConfig(() => ({
              ...prev,
              view: ViewStateType.DEFAULT,
            })),
        }));
      } else {
        setSlideOutConfig((prev) => ({
          ...prev,
          view: ViewStateType.ERROR_RESULT,
          templateConfig: { subTitle: errorMessage || 'Something went wrong. Please try again.' },
          onCTA: () =>
            setSlideOutConfig(() => ({
              ...prev,
              view: ViewStateType.DEFAULT,
            })),
        }));
      }
    } finally {
      setTimeout(() => {
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS] });
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS] });
        if (slideOutConfig?.searchText) {
          queryClient.resetQueries({
            queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS_SEARCH, slideOutConfig.searchText],
          });
          queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH, slideOutConfig.searchText] });
        }
        // only works if they are separately called
      }, QUERY_REFECH_TIMEOUT);
    }
  };

  const cancelOffer = async (offer?: OffersType) => {
    const deleteOfferParams: IDeleteOffersParams = {
      headers: {
        emailAddress: 'email',
        operationType: 'DeleteSellOrder',
      },
      orderId: offer?.id as string,
    };

    try {
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.LOADING,
      }));
      await mutateDeleteOffer(deleteOfferParams);
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.THANK_YOU,
        templateConfig: { translationKey: 'common:offerCanceled' },
        isOpen: true,
      }));
    } catch (error) {
      setSlideOutConfig((prev) => ({
        ...prev,
        view: ViewStateType.ERROR_RESULT,
        onCTA: () =>
          setSlideOutConfig(() => ({
            ...prev,
            view: ViewStateType.DEFAULT,
          })),
      }));
    }

    setTimeout(() => {
      // only works if they are separately called
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_STOCK] });
      if (slideOutConfig?.searchText) {
        queryClient.resetQueries({
          queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS_SEARCH, slideOutConfig.searchText],
        });
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH, slideOutConfig.searchText] });
      }
    }, QUERY_REFECH_TIMEOUT);
  };

  const disableButton = () => {
    return (
      slideOutConfig?.offer?.stopLimitPriceInTargetCurrency === stopLimitText ||
      stopLimitText < 1 ||
      stopLimitText > (slideOutConfig?.offer?.marketValueInTargetCurrency || 0) ||
      !stopLimitText
    );
  };

  const formatterToTargetCurrency = useMemo((): CurrencyFormater => {
    return {
      format: (value: number, isNew = true) => {
        return formatter.format(value, isNew);
      },
    };
  }, [formatter]);

  return (
    <FeedbackTemplate
      viewState={slideOutConfig?.view || ViewStateType.DEFAULT}
      templateConfig={slideOutConfig.templateConfig}
      onCTA={slideOutConfig.onCTA}
      onClose={() => {
        setSlideOutConfig({ isOpen: false });
      }}
    >
      <WineInfoTemplate config={slideOutConfig.wineInfoTemplateConfig}>
        <div className="flex flex-col flex-1 bg-white dark:bg-slate-900">
          {/* Input Card */}
          <div className="px-4 pt-3">
            <span className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5 block">
              {displayText[DisplayTextKeys.ENTER_NEW_STOP_LIMIT]}
            </span>
            <CustomInput
              type={DisplayFieldType.CURRENCY}
              formatter={formatterToTargetCurrency}
              value={stopLimitText}
              onChange={(e) => {
                setStopLimitText(Number(e.target.value));
              }}
              placeholder="0.00"
              name="Stop limit"
              className="flex w-full border border-gray-200 dark:border-slate-600 rounded-lg items-center bg-white dark:bg-slate-700 hover:border-orange/50 dark:hover:border-teal-500/50 focus-within:border-orange dark:focus-within:border-teal-500 transition-colors [&>div]:border-0"
              inputClassName="text-gray-900 dark:text-white p-3 bg-transparent placeholder:text-gray-400 dark:placeholder:text-slate-500"
            />
            {stopLimitText > (slideOutConfig?.offer?.marketValueInTargetCurrency || 0) ? (
              <p className="text-xs text-red-500 dark:text-red-400 mt-1.5">
                {displayText[DisplayTextKeys.STOP_LIMIT_MESSAGE]}
              </p>
            ) : null}
          </div>

          {/* Values Card */}
          <div className="px-4 py-3">
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 border border-gray-100 dark:border-slate-700/50">
              <div className="flex justify-between divide-x divide-gray-200 dark:divide-slate-700">
                <div className="flex-1 flex flex-col items-center pr-3">
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                    {displayText[DisplayTextKeys.TOTAL_MARKET_VALUE]}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white font-semibold">
                    {formatter.format(slideOutConfig?.offer?.marketValueInTargetCurrency || 0, true)}
                  </span>
                </div>
                <div className="flex-1 flex flex-col items-center pl-3">
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                    {displayText[DisplayTextKeys.STOP_LIMIT]}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white font-semibold">
                    {formatter.format(stopLimitText || 0, true)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-4 pb-4 mt-auto flex flex-col gap-2">
            <Button
              isProcessing={editOfferIsLoading}
              isDisable={disableButton() || editOfferIsLoading}
              onClick={() => updateOffer(slideOutConfig?.offer as OffersType, accountInfo?.account.fxRate || 1)}
              type="button"
              className="w-full h-11 text-sm font-semibold rounded-full btn bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-md shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-lg disabled:opacity-50 disabled:shadow-none transition-all"
            >
              {displayText[DisplayTextKeys.UPDATE]}
            </Button>
            <button
              className="h-9 text-red-500 dark:text-red-400 text-sm font-medium hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
              onClick={() => cancelOffer(slideOutConfig.offer)}
            >
              {displayText[DisplayTextKeys.CANCEL]}
            </button>
          </div>
        </div>
      </WineInfoTemplate>
    </FeedbackTemplate>
  );
}; // Price exceed market price amount of £{maximumAmount} which is 5% below market price.

export default EditOfferView;
